import React from "react";
import { ActivityIndicator, Pressable, Text, View } from "react-native";
import Ionicons from "react-native-vector-icons/Ionicons";
import Svg, { Path } from "react-native-svg";

import type {
  TCommunicationsCall,
  TCommunicationsCallId,
} from "../../../../../services/api-communications";
import { ADMIN_TOKENS } from "../../../../../styles/adminTokens";
import { PhoneNumberText } from "../../../../common/native";
import { styles } from "../HomeConversations.styles";
import { getCallStatusBadgeStyle } from "../utils/formatters";

const toStatusLabel = (status?: string | null): string => {
  const normalizedStatus = (status || "").trim().toLowerCase();
  if (!normalizedStatus) {
    return "Unknown";
  }
  return normalizedStatus.replace(/[_-]+/g, " ");
};

const getCallStatusIcon = (
  status?: string | null,
  direction?: string | null,
  color?: string,
) => {
  const normalizedStatus = (status || "").trim().toLowerCase();
  const normalizedDirection = (direction || "").trim().toLowerCase();
  const iconColor = color || ADMIN_TOKENS.color.textSecondary;

  if (normalizedStatus === "completed") {
    return (
      <Svg
        width={ADMIN_TOKENS.fontSize.md}
        height={ADMIN_TOKENS.fontSize.md}
        viewBox="0 -960 960 960"
        fill={iconColor}
        accessibilityLabel="Completed"
      >
        <Path d="M268-240 42-466l57-56 170 170 56 56-57 56Zm226 0L268-466l56-57 170 170 368-368 56 57-424 424Zm0-226-57-56 198-198 57 56-198 198Z" />
      </Svg>
    );
  }

  if (["no_answer", "no-answer", "busy"].includes(normalizedStatus)) {
    const isInbound = normalizedDirection === "inbound";
    return isInbound ? (
      <Svg
        width={ADMIN_TOKENS.fontSize.md}
        height={ADMIN_TOKENS.fontSize.md}
        viewBox="0 -960 960 960"
        fill={iconColor}
        accessibilityLabel="Incoming not answered"
      >
        <Path d="M480-263 200-543v183h-80v-320h320v80H256l224 224 304-304 56 57-360 360Z" />
      </Svg>
    ) : (
      <Svg
        width={ADMIN_TOKENS.fontSize.md}
        height={ADMIN_TOKENS.fontSize.md}
        viewBox="0 -960 960 960"
        fill={iconColor}
        accessibilityLabel="Outgoing not answered"
      >
        <Path d="M480-263 120-623l56-57 304 304 224-224H520v-80h320v320h-80v-183L480-263Z" />
      </Svg>
    );
  }

  if (["failed", "canceled", "cancelled"].includes(normalizedStatus)) {
    return (
      <Svg
        width={ADMIN_TOKENS.fontSize.md}
        height={ADMIN_TOKENS.fontSize.md}
        viewBox="0 -960 960 960"
        fill={iconColor}
        accessibilityLabel="Call failed"
      >
        <Path d="M792-52 570-274q-89 72-193.5 113T162-120q-24 0-33-12t-9-30v-162q0-14 9-24.5t23-13.5l138-28q11-2 27.5 3t24.5 13l94 94q18-11 39-25t37-27L56-788l56-56 736 736-56 56ZM360-244l-66-66-94 20v88q41-3 81-14t79-28Zm322-144-56-56q15-17 30.5-39t24.5-41l-97-98q-8-8-11-22.5t-1-23.5l26-140q3-14 13.5-23t24.5-9h162q18 0 30 12t12 30q0 110-42 214.5T682-388Zm36-212q17-39 26-79t14-81h-88l-18 94 66 66Zm0 0ZM360-244Z" />
      </Svg>
    );
  }

  return (
    <Svg
      width={ADMIN_TOKENS.fontSize.md}
      height={ADMIN_TOKENS.fontSize.md}
      viewBox="0 -960 960 960"
      fill={iconColor}
      accessibilityLabel={toStatusLabel(status)}
    >
      <Path d="M80-440v-80h240v80H80Zm560 280-57-56 183-184H480v-80h286L583-664l57-56 280 280-280 280ZM80-680v-80h400v80H80Zm0 480v-80h400v80H80Z" />
    </Svg>
  );
};

type CallsInboxCardProps = {
  isCallsTab: boolean;
  refreshingCalls: boolean;
  calls: TCommunicationsCall[];
  assignedNumberOptions: string[];
  selectedAssignedNumberE164: string | null;
  selectedCallId: TCommunicationsCallId | null;
  assignSubmitting: boolean;
  hasMoreCalls: boolean;
  loadingMoreCalls: boolean;
  callsTotal: number;
  callsPaginationError: string | null;
  canStartCall: boolean;
  onAssignedNumberChange: (value: string | null) => void;
  onSelectCallId: (callId: TCommunicationsCallId) => void;
  onOpenAssignPatientModal: (call: TCommunicationsCall) => void;
  onCallAgain: (call: TCommunicationsCall) => void;
  onLoadMoreCalls: () => void;
  getPatientNameForCall: (patientUuid?: string | null) => string;
  resolveRedialFromNumber: (
    requestedFromE164: string | null | undefined,
  ) => string;
  formatDateTime: (value?: string | null) => string;
  formatPhoneNumberDisplay: (value?: string | null) => string;
};

const CallsInboxCard: React.FC<CallsInboxCardProps> = ({
  isCallsTab,
  refreshingCalls,
  calls,
  assignedNumberOptions,
  selectedAssignedNumberE164,
  selectedCallId,
  assignSubmitting,
  hasMoreCalls,
  loadingMoreCalls,
  callsTotal,
  callsPaginationError,
  canStartCall,
  onAssignedNumberChange,
  onSelectCallId,
  onOpenAssignPatientModal,
  onCallAgain,
  onLoadMoreCalls,
  getPatientNameForCall,
  resolveRedialFromNumber,
  formatDateTime,
  formatPhoneNumberDisplay,
}) => {
  const [assignedNumberDropdownOpen, setAssignedNumberDropdownOpen] =
    React.useState<boolean>(false);
  const selectedAssignedNumberLabel = selectedAssignedNumberE164
    ? formatPhoneNumberDisplay(selectedAssignedNumberE164)
    : "All Numbers";

  return (
    <View style={[styles.card, !isCallsTab && styles.hiddenSection]}>
      <View style={styles.rowBetween}>
        <Text style={styles.cardTitle}>Calls Inbox</Text>
        {refreshingCalls ? (
          <ActivityIndicator size="small" color={ADMIN_TOKENS.color.accent} />
        ) : null}
      </View>
      <View style={[styles.messagesFilterGroup, styles.dropdownLayerHost]}>
        {/* Keep this host above list rows; if z-index/overflow is removed, the dropdown menu renders behind inbox items. */}
        <Text style={styles.messagesFilterLabel}>Assigned Number</Text>
        <View
          style={[
            styles.assignedNumberDropdownContainer,
            assignedNumberDropdownOpen &&
              styles.assignedNumberDropdownContainerOpen,
          ]}
        >
          <Pressable
            style={[
              styles.inlineOptionButton,
              styles.assignedNumberDropdownTrigger,
              assignedNumberDropdownOpen && styles.inlineOptionButtonActive,
            ]}
            onPress={() =>
              setAssignedNumberDropdownOpen((currentValue) => !currentValue)
            }
            accessibilityLabel="Toggle assigned number filters for calls"
          >
            <Text
              style={[
                styles.inlineOptionText,
                assignedNumberDropdownOpen && styles.inlineOptionTextActive,
              ]}
            >
              {selectedAssignedNumberLabel}
            </Text>
            <Ionicons
              name={assignedNumberDropdownOpen ? "chevron-up" : "chevron-down"}
              size={14}
              color={ADMIN_TOKENS.color.textSecondary}
            />
          </Pressable>
          {assignedNumberDropdownOpen ? (
            <View style={styles.assignedNumberDropdownMenu}>
              <Pressable
                style={styles.assignedNumberDropdownItem}
                onPress={() => {
                  onAssignedNumberChange(null);
                  setAssignedNumberDropdownOpen(false);
                }}
                accessibilityLabel="Filter calls for all assigned numbers"
              >
                <Text
                  style={[
                    styles.inlineOptionText,
                    !selectedAssignedNumberE164 &&
                      styles.inlineOptionTextActive,
                  ]}
                >
                  All Numbers
                </Text>
              </Pressable>
              {assignedNumberOptions.map((numberE164) => {
                const formattedNumber = formatPhoneNumberDisplay(numberE164);
                const isSelected = selectedAssignedNumberE164 === numberE164;
                return (
                  <Pressable
                    key={numberE164}
                    style={styles.assignedNumberDropdownItem}
                    onPress={() => {
                      onAssignedNumberChange(numberE164);
                      setAssignedNumberDropdownOpen(false);
                    }}
                    accessibilityLabel={`Filter calls for ${formattedNumber}`}
                  >
                    <Text
                      style={[
                        styles.inlineOptionText,
                        isSelected && styles.inlineOptionTextActive,
                      ]}
                    >
                      {formattedNumber}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          ) : null}
        </View>
      </View>

      {!calls.length ? (
        <Text style={styles.emptyText}>
          No calls yet. Start an outbound call to populate this list.
        </Text>
      ) : (
        <View style={styles.listContainer}>
          {calls.map((call) => {
            const badge = getCallStatusBadgeStyle(call.status);
            const redialFromNumber = resolveRedialFromNumber(call.from_e164);
            const patientName = getPatientNameForCall(call.patient_uuid);
            const formattedToE164 = formatPhoneNumberDisplay(call.to_e164);
            const isUnassignedPatient = patientName === "Unassigned";
            const assignActionLabel = isUnassignedPatient ? "Assign" : "Edit";
            const canCallAgain =
              canStartCall &&
              !!(call.to_e164 || "").trim() &&
              !!redialFromNumber;

            return (
              <View
                key={call.call_uuid}
                style={[
                  styles.callRow,
                  selectedCallId === call.call_uuid && styles.callRowSelected,
                ]}
              >
                <Pressable
                  style={styles.callRowContent}
                  onPress={() => onSelectCallId(call.call_uuid)}
                >
                  <View style={styles.rowBetween}>
                    <View style={styles.callHeaderLeft}>
                      <Text style={styles.callNumberText}>
                        {formattedToE164}
                      </Text>
                      {patientName !== "Unassigned" ? (
                        <View style={styles.callNameWithEditWrap}>
                          <Text
                            style={[
                              styles.metaText,
                              styles.callPatientNameText,
                            ]}
                            numberOfLines={1}
                            ellipsizeMode="tail"
                          >
                            • {patientName}
                          </Text>
                          <Pressable
                            style={[
                              styles.inlineIconButton,
                              assignSubmitting &&
                                styles.callAgainButtonDisabled,
                            ]}
                            onPress={() => onOpenAssignPatientModal(call)}
                            disabled={assignSubmitting}
                            accessibilityLabel={`${assignActionLabel} patient assignment for call ${call.call_uuid}`}
                          >
                            <Svg
                              width={16}
                              height={16}
                              viewBox="0 -960 960 960"
                              fill={ADMIN_TOKENS.color.textSecondary}
                              accessibilityLabel={`${assignActionLabel} patient`}
                            >
                              <Path d="M200-200h57l391-391-57-57-391 391v57Zm-80 80v-170l528-527q12-11 26.5-17t30.5-6q16 0 31 6t26 18l55 56q12 11 17.5 26t5.5 30q0 16-5.5 30.5T817-647L290-120H120Zm640-584-56-56 56 56Zm-141 85-28-29 57 57-29-28Z" />
                            </Svg>
                          </Pressable>
                        </View>
                      ) : null}
                    </View>
                    <View
                      accessibilityLabel={`Call status: ${toStatusLabel(call.status)}`}
                    >
                      {getCallStatusIcon(
                        call.status,
                        call.direction,
                        badge.textColor,
                      )}
                    </View>
                  </View>
                  <View style={styles.callMetaInlineRow}>
                    <View style={styles.callMetaInlineLeft}>
                      <Text style={styles.metaText}>
                        From:{" "}
                        <PhoneNumberText
                          value={call.from_e164}
                          style={styles.metaText}
                        />
                      </Text>
                      <Text style={styles.metaText}>
                        Time: {formatDateTime(call.updated_at)}
                      </Text>
                    </View>
                    <Pressable
                      style={[
                        styles.callAgainIconButton,
                        !canCallAgain && styles.callAgainButtonDisabled,
                      ]}
                      onPress={() => onCallAgain(call)}
                      disabled={!canCallAgain}
                      accessibilityLabel={`Call ${formatPhoneNumberDisplay(call.to_e164) === "-" ? "number" : formatPhoneNumberDisplay(call.to_e164)} again`}
                    >
                      <Svg
                        width={20}
                        height={20}
                        viewBox="0 -960 960 960"
                        fill={
                          canCallAgain
                            ? ADMIN_TOKENS.color.accentDark
                            : ADMIN_TOKENS.color.textMuted
                        }
                        accessibilityLabel="Call again"
                      >
                        <Path d="M798-120q-125 0-247-54.5T329-329Q229-429 174.5-551T120-798q0-18 12-30t30-12h162q14 0 25 9.5t13 22.5l26 140q2 16-1 27t-11 19l-97 98q20 37 47.5 71.5T387-386q31 31 65 57.5t72 48.5l94-94q9-9 23.5-13.5T670-390l138 28q14 4 23 14.5t9 23.5v162q0 18-12 30t-30 12ZM241-600l66-66-17-94h-89q5 41 14 81t26 79Zm358 358q39 17 79.5 27t81.5 13v-88l-94-19-67 67ZM241-600Zm358 358Z" />
                      </Svg>
                    </Pressable>
                  </View>
                </Pressable>
              </View>
            );
          })}
          {hasMoreCalls ? (
            <Pressable
              style={[
                styles.callAgainButton,
                styles.callsPaginationButton,
                (loadingMoreCalls || refreshingCalls) &&
                  styles.callAgainButtonDisabled,
              ]}
              onPress={onLoadMoreCalls}
              disabled={loadingMoreCalls || refreshingCalls}
              accessibilityLabel="Load older calls"
            >
              {loadingMoreCalls ? (
                <View style={styles.loadingInlineRow}>
                  <ActivityIndicator
                    size="small"
                    color={ADMIN_TOKENS.color.accentDark}
                  />
                  <Text style={styles.callAgainButtonText}>Loading...</Text>
                </View>
              ) : (
                <Text style={styles.callAgainButtonText}>
                  Load Older Calls ({calls.length}/{callsTotal})
                </Text>
              )}
            </Pressable>
          ) : null}
          {callsPaginationError ? (
            <Text style={styles.inlineErrorText}>{callsPaginationError}</Text>
          ) : null}
        </View>
      )}
    </View>
  );
};

export default CallsInboxCard;
